import { create } from "zustand";

import type { NotificationStack as NotificationStackType } from "@/constants/notification";
import {
  NotificationStackId,
  NOTIFICATION_STACKS,
} from "@/constants/notification";

export enum NotificationType {
  SUCCESS = "success",
  WARNING = "warning",
  DISABLED = "disabled",
  LOCKED = "locked",
  NEXT_BITE = "next-bite",
  EXIT_LESSON = "exit-lesson",
  SPACE_STUDENT = "space-student",
  CHANNEL_WARNING = "channel-warning",
}

/**
 * A single button offered alongside the message, for the seconds the toast is
 * up. Undo is the reason this exists: a bulk write the teacher can reverse
 * without hunting for the control that made it.
 */
export interface NotificationAction {
  label: string;
  onClick: () => void;
}

export interface Notification {
  id: string;
  message: string;
  type: NotificationType;
  timestamp: number;
  count?: number;
  stack: NotificationStackType;
  title?: string;
  titleTemplate?: string;
  titleData?: Record<string, any>;
  subheading?: string;
  durationMs?: number;
  action?: NotificationAction;
}

interface NotificationStore {
  notifications: Notification[];
  timeoutIds: Map<string, NodeJS.Timeout>;
  promiseResolvers: Map<string, (value: boolean) => void>;
  isSlideChangeNotificationActive: boolean;
  slideChangeLockCount: number;
  slideChangeCountdownCount: number;
  acquireSlideChangeLock: (options?: { isCountdown?: boolean }) => void;
  releaseSlideChangeLock: (options?: { isCountdown?: boolean }) => void;
  showNotification: (
    message: string,
    type?: NotificationType,
    duration?: number,
    stack?: NotificationStackType,
    action?: NotificationAction,
  ) => void;
  showTutorViewNotification: (
    titleTemplate: string,
    titleData: Record<string, any>,
    durationMs: number,
    subheading?: string,
    type?: NotificationType,
    stack?: NotificationStackType,
    notificationId?: string,
    promiseResolver?: (value: boolean) => void,
  ) => string;
  removeNotification: (id: string) => void;
  getNotificationsByStackId: (stackId: string) => Notification[];
  cancelAllNotifications: () => void;
}

export const useNotificationStore = create<NotificationStore>((set, get) => ({
  notifications: [],
  timeoutIds: new Map(),
  promiseResolvers: new Map(),
  isSlideChangeNotificationActive: false,
  // Refcounted: the countdown toast and the transition showing it hold the lock
  // over overlapping spans, and a boolean lets whichever ends first unlock the
  // button while the other is still navigating.
  slideChangeLockCount: 0,
  // The countdown alone: cancellable, unlike the transition's hold after it.
  slideChangeCountdownCount: 0,
  acquireSlideChangeLock: ({ isCountdown = false } = {}) =>
    set((state) => {
      const count = state.slideChangeLockCount + 1;
      const countdownCount =
        state.slideChangeCountdownCount + (isCountdown ? 1 : 0);
      return {
        slideChangeLockCount: count,
        isSlideChangeNotificationActive: count > 0,
        slideChangeCountdownCount: countdownCount,
      };
    }),
  releaseSlideChangeLock: ({ isCountdown = false } = {}) =>
    set((state) => {
      const count = Math.max(0, state.slideChangeLockCount - 1);
      const countdownCount = Math.max(
        0,
        state.slideChangeCountdownCount - (isCountdown ? 1 : 0),
      );
      return {
        slideChangeLockCount: count,
        isSlideChangeNotificationActive: count > 0,
        slideChangeCountdownCount: countdownCount,
      };
    }),
  showNotification: (
    message,
    type = NotificationType.SUCCESS,
    duration = 5000,
    stack,
    action,
  ) => {
    const selectedStack =
      stack || NOTIFICATION_STACKS[NotificationStackId.BOTTOM_CENTER];
    const existing = get().notifications.find(
      (n) =>
        n.message === message &&
        n.type === type &&
        n.stack.id === selectedStack.id,
    );
    const id = existing?.id ?? Math.random().toString(36).substr(2, 9);
    // A repeat restarts the clock. The dismissal timer used to be left running
    // from the first call, so a second notification arriving late in the window
    // inherited whatever was left of it -- a repeat at 11s of a 12s toast was
    // readable for about a second, and any Undo it carried went with it.
    const running = get().timeoutIds.get(id);
    if (running) clearTimeout(running);
    const timeoutId = setTimeout(() => {
      get().removeNotification(id);
    }, duration);
    set((state) => {
      const timeoutIds = new Map(state.timeoutIds).set(id, timeoutId);
      if (existing) {
        return {
          notifications: state.notifications.map((n) =>
            n.id === id
              ? {
                  ...n,
                  count: (n.count || 1) + 1,
                  timestamp: Date.now(),
                  // The later call's action, not the first one's: repeating an
                  // identical message means a second write happened, and Undo
                  // has to reverse that one.
                  action,
                }
              : n,
          ),
          timeoutIds,
        };
      }
      const newNotification: Notification = {
        id,
        message,
        type,
        timestamp: Date.now(),
        count: 1,
        stack: selectedStack,
        action,
      };
      return {
        notifications: [...state.notifications, newNotification],
        timeoutIds,
      };
    });
  },
  showTutorViewNotification: (
    titleTemplate,
    titleData,
    durationMs,
    subheading = "",
    type = NotificationType.NEXT_BITE,
    stack,
    notificationId,
    promiseResolver,
  ) => {
    const selectedStack =
      stack || NOTIFICATION_STACKS[NotificationStackId.NEXT_SLIDE];
    const id = notificationId || Math.random().toString(36).substr(2, 9);
    const timeoutId = setTimeout(() => {
      promiseResolver?.(true);
      set((state) => {
        const newTimeoutIds = new Map(state.timeoutIds);
        newTimeoutIds.delete(id);
        const newPromiseResolvers = new Map(state.promiseResolvers);
        newPromiseResolvers.delete(id);
        return {
          notifications: state.notifications.filter((n) => n.id !== id),
          timeoutIds: newTimeoutIds,
          promiseResolvers: newPromiseResolvers,
        };
      });
    }, durationMs);

    set((state) => {
      const newNotification: Notification = {
        id,
        message: titleTemplate,
        type,
        timestamp: Date.now(),
        count: 1,
        stack: selectedStack,
        titleTemplate,
        titleData,
        subheading,
        durationMs,
      };
      const newTimeoutIds = new Map(state.timeoutIds);
      newTimeoutIds.set(id, timeoutId);
      const newPromiseResolvers = promiseResolver
        ? new Map(state.promiseResolvers).set(id, promiseResolver)
        : state.promiseResolvers;
      return {
        notifications: [...state.notifications, newNotification],
        timeoutIds: newTimeoutIds,
        promiseResolvers: newPromiseResolvers,
      };
    });
    return id;
  },
  removeNotification: (id) =>
    set((state) => {
      const timeoutId = state.timeoutIds.get(id);
      const promiseResolver = state.promiseResolvers.get(id);

      if (timeoutId) {
        clearTimeout(timeoutId);
      }

      if (promiseResolver) {
        promiseResolver(false);
      }

      const newTimeoutIds = new Map(state.timeoutIds);
      newTimeoutIds.delete(id);
      const newPromiseResolvers = new Map(state.promiseResolvers);
      newPromiseResolvers.delete(id);

      return {
        notifications: state.notifications.filter((n) => n.id !== id),
        timeoutIds: newTimeoutIds,
        promiseResolvers: newPromiseResolvers,
      };
    }),
  getNotificationsByStackId: (stackId) => {
    const state = get();
    return state.notifications.filter((n) => n.stack.id === stackId);
  },
  cancelAllNotifications: () => {
    set((state) => {
      state.timeoutIds.forEach((timeoutId) => {
        clearTimeout(timeoutId);
      });
      state.promiseResolvers.forEach((resolver) => {
        resolver(false);
      });
      return {
        notifications: [],
        timeoutIds: new Map(),
        promiseResolvers: new Map(),
      };
    });
  },
}));

export function showDynamicTutorViewNotification(
  titleTemplate: string,
  def: Record<string, any>,
  subheading: string,
  type: NotificationType,
  durationMs: number,
  stack: NotificationStackType,
): Promise<boolean> {
  return new Promise((resolve) => {
    const notificationId = Math.random().toString(36).substr(2, 9);
    const store = useNotificationStore.getState();

    const locksSlideChange =
      type === NotificationType.NEXT_BITE ||
      type === NotificationType.EXIT_LESSON;

    if (locksSlideChange) {
      store.acquireSlideChangeLock({ isCountdown: true });
    }

    store.showTutorViewNotification(
      titleTemplate,
      def,
      durationMs,
      subheading,
      type,
      stack,
      notificationId,
      (completed) => {
        if (locksSlideChange) {
          store.releaseSlideChangeLock({ isCountdown: true });
        }
        resolve(completed);
      },
    );
  });
}
